import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdInput, CmdLabel, CmdLoader, CmdSimpleSelect } from '@commander-services/gui-components';
import { Controller, FieldError, get, useFieldArray, useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AreaTypes,
  IAddWaypointRequestData,
  IFormWaypointResponseData,
  IWaypoint,
  IWaypointPostion,
  translateOptions,
} from './types';
import showMessage from '../../Toastr/ToastService';
import PermissionsService, {
  PERMISSION_WAYPOINT_CUSTOMER,
  PERMISSION_WAYPOINT_PRIVATE,
} from '../../../Services/PermissionsService';
import ConfirmDialog from '../../ConfirmDialog';
import {
  convertCoordinatesToArray,
  createWaypoint,
  filterBinary,
  getWaypointFormData,
  getWaypointList,
} from './WaypointService';
import { ISimpleSelectOption } from '../../Forms/ArrayFields/interfaces';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import SimpleSelectWithIcon from '../../Forms/ReactHookFormFields/SimpleSelectWithIcon';
import { WAYPOINT_QUERY_KEY } from './constants';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  activeFilterAtom,
  centerWaypointAtom,
  computedAreaAtom,
  coordinatesAtom,
  filteredWaypointsAtom,
  filteredWaypointsStringAtom,
  isDrawWaypointAtom,
  waypointIdAtom,
  waypointsAtom,
} from './WaypointState';
import { STATUS_WARNING } from '../../../Services/HttpService';

interface IAddWaypointForm {
  closeAddWaypoint: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  saveAddWaypoint: () => void;
}

interface AddWaypointFormValues {
  icon: string;
  name: string;
  type: string;
  customer: string;
  areaType: AreaTypes | '';
  refuelingTypeList: string;
  fuels?: {
    fuelTypeId: string;
    unitPriceIncVat: string;
  }[];
}

export default function AddWaypointForm(props: IAddWaypointForm): JSX.Element {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const intlShape = useIntl();
  const { formatMessage: f } = intlShape;
  const [waypoints, setWaypoints] = useRecoilState(waypointsAtom);
  const filteredWaypointsString = useRecoilValue<string>(filteredWaypointsStringAtom);
  const setFilteredWaypoints = useSetRecoilState<IWaypoint[]>(filteredWaypointsAtom);
  const computedArea = useRecoilState<number>(computedAreaAtom)[0];
  const activeFilter = useRecoilValue(activeFilterAtom);
  const [formOptions, setFormOptions] = React.useState<IFormWaypointResponseData | null>(null);
  const [customerFormData, setCustomerFormData] = React.useState<ISimpleSelectOption[] | null>(
    null
  );
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = React.useState(false);
  const coordinates = useRecoilValue<IWaypointPostion[] | null>(coordinatesAtom);
  const center = useRecoilValue<IWaypointPostion | null>(centerWaypointAtom);
  const setIsDrawWaypoint = useSetRecoilState<boolean>(isDrawWaypointAtom);
  const setWaypointId = useSetRecoilState<number | null>(waypointIdAtom);
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<AddWaypointFormValues>({
    mode: 'onChange',
    defaultValues: {
      icon: '',
      name: '',
      type: '',
      customer:
        selectedCustomers.length === 1 && customerFormData ? String(selectedCustomers[0]) : '',
      areaType: '',
      refuelingTypeList: '',
      fuels: [
        {
          fuelTypeId: '',
          unitPriceIncVat: '',
        },
      ],
    },
    resolver: async (data) => {
      const formValidation = {
        customer: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        icon: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        name: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
          {
            param: 'maxLength',
            value: 100,
            message: f({ id: 'form.maxLength' }, { value: 100 }),
          },
        ],
        type: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      } as Record<string, { param: string; value: boolean; message: string }[]>;

      if (data.areaType && data.areaType !== 'waypoint') {
        formValidation.refuelingTypeList = [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ];
      }

      const validateErrors = validateFields(data, formValidation);

      let formErrors: Record<string, FieldError> = transformErrors(validateErrors);

      if (data.name && data.name.trim().length <= 4) {
        formErrors = {
          ...formErrors,
          name: { type: 'required', message: f({ id: 'waypoints.edit.title.required.min.char' }) },
        };
      }
      if (
        data.customer &&
        data.type === 'private' &&
        !PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_PRIVATE,
          ['create'],
          Number(data.customer)
        )
      ) {
        formErrors = {
          ...formErrors,
          type: { type: 'required', message: f({ id: 'waypoints.edit.title.required' }) },
        };
      }
      if (
        data.customer &&
        data.type === 'customer' &&
        !PermissionsService.hasAccess(
          PERMISSION_WAYPOINT_CUSTOMER,
          ['create'],
          Number(data.customer)
        )
      ) {
        formErrors = {
          ...formErrors,
          type: { type: 'required', message: f({ id: 'waypoints.edit.title.required' }) },
        };
      }

      if (data.areaType && data.areaType !== 'waypoint' && Array.isArray(data.fuels)) {
        data.fuels.forEach((fuel, index) => {
          if (!fuel.fuelTypeId) {
            formErrors[`fuels.${index}.fuelTypeId`] = {
              type: 'required',
              message: f({ id: 'form.required' }),
            };
          }

          if (
            fuel.unitPriceIncVat === '' ||
            fuel.unitPriceIncVat === null ||
            fuel.unitPriceIncVat === undefined
          ) {
            formErrors[`fuels.${index}.unitPriceIncVat`] = {
              type: 'required',
              message: f({ id: 'form.required' }),
            };
          } else {
            const value = parseFloat(fuel.unitPriceIncVat);
            if (Number.isNaN(value) || value < 0) {
              formErrors[`fuels.${index}.unitPriceIncVat`] = {
                type: 'min',
                message: f({ id: 'form.rules.minValue' }, { value: 0 }),
              };
            } else if (value > 999999.99) {
              formErrors[`fuels.${index}.unitPriceIncVat`] = {
                type: 'max',
                message: f({ id: 'form.rules.maxValue' }, { value: 999999.99 }),
              };
            }
          }
        });
      }

      const finalErrors = {
        ...transformErrors(validateErrors),
        ...formErrors,
      };

      return {
        values: Object.keys(finalErrors).length ? {} : data,
        errors: finalErrors,
      };
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: 'fuels',
    control,
    rules: { maxLength: formOptions?.fuelTypeList.length ?? 0 },
  });

  const handleAddFuel = () => {
    append({
      fuelTypeId: '',
      unitPriceIncVat: '',
    });
  };

  const handleRemoveFuel = (index: number) => {
    remove(index);
  };

  const updateWaypoints = () => {
    queryClient.invalidateQueries({
      queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers, activeFilter],
    });
  };

  const handleFormOptions = async () => {
    const response = await getWaypointFormData();
    setCustomerFormData(null);

    setFormOptions(null);

    if (response) {
      const formDataCustomer: ISimpleSelectOption[] = response.customerId.values.filter(
        (item) =>
          selectedCustomers.includes(Number(item.value)) &&
          (PermissionsService.hasAccess(
            PERMISSION_WAYPOINT_CUSTOMER,
            ['create'],
            Number(item.value)
          ) ||
            PermissionsService.hasAccess(
              PERMISSION_WAYPOINT_PRIVATE,
              ['create'],
              Number(item.value)
            ))
      );
      if (selectedCustomers.length === 1) {
        setValue('customer', String(selectedCustomers[0]));
      }

      setCustomerFormData(formDataCustomer);

      if (response.type) {
        const formDataTooltip = response.type.values.map((item) => {
          if (item.value === 'customer') {
            return { ...item, tooltip: f({ id: 'waypoints.form.customer.type.tooltip' }) };
          }
          return { ...item, tooltip: f({ id: 'waypoints.form.private.type.tooltip' }) };
        });

        setFormOptions({ ...response, type: { values: formDataTooltip } });
      } else {
        setFormOptions(response);
      }
    }
  };

  const { isLoading } = useQuery({
    queryKey: ['addWaypointFormOptions', selectedCustomers],
    queryFn: () => handleFormOptions(),
    // staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleOnSubmit = async (values: AddWaypointFormValues) => {
    const trimName = values.name.trim();

    if (Object.keys(errors).length > 0) {
      return;
    }

    if (coordinates && center) {
      updateWaypoints();

      const baseData: IAddWaypointRequestData = {
        name: trimName,
        type: values.type,
        centerPoint: center,
        customerId: Number(values.customer),
        area: computedArea,
        categoryId: Number(values.icon),
        coordinates: convertCoordinatesToArray(coordinates, true),
        waypoint: values.areaType !== 'fuelArea',
      };

      let data: IAddWaypointRequestData = baseData;

      if (
        values.areaType !== 'waypoint' &&
        values.areaType &&
        typeof values.refuelingTypeList === 'string' &&
        Array.isArray(values.fuels) &&
        values.fuels.length > 0
      ) {
        data = {
          ...baseData,
          refueling: {
            type: values.refuelingTypeList,
            fuels: values.fuels.map((fuel) => ({
              fuelTypeId: Number(fuel.fuelTypeId),
              unitPriceIncVat: Number(fuel.unitPriceIncVat),
            })),
          },
        };
      }

      const newWaypoint = await createWaypoint(data);

      if (newWaypoint && newWaypoint?.coordinates) {
        const waypointsList = await getWaypointList(
          activeFilter ? { filters: { ...activeFilter } } : null
        );
        if (waypointsList) {
          setIsDrawWaypoint(false);

          setWaypoints(waypointsList.waypoints);
          setFilteredWaypoints(filterBinary(filteredWaypointsString, waypoints));
          setWaypointId(Number(newWaypoint.id));
          props.saveAddWaypoint();
        }
      }
      if (newWaypoint && newWaypoint.status === STATUS_WARNING) {
        const message = newWaypoint?.toast?.message;

        if (message) {
          setError('name', {
            type: 'required',
            message: f({ id: `${message}` }),
          });
        }
      }
    } else {
      showMessage(
        f({ id: 'waypoints.draw.warning.message.title' }),
        f({ id: 'waypoints.draw.warning.message' }, { name: trimName }),
        'warning'
      );
    }
  };

  const handleConfirmDialog = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setIsOpenConfirmDialog(true);
  };
  const handleContinueConfirmDialog = () => {
    setIsOpenConfirmDialog(false);

    props.closeAddWaypoint();
  };

  const handleCloseConfirmDialog = () => {
    setIsOpenConfirmDialog(false);
  };

  const fuelIcon = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <g id="Icons/16px/add_fuel" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <path
            d="M10.6666667,0 C11.7695333,0 12.6666667,0.978690909 12.6666667,2.18181818 L12.665786,8.01102637 C11.41985,8.09349427 10.2970651,8.6336328 9.46534894,9.46534894 C8.55988567,10.3708122 8,11.62125 8,13 C8,14.1247937 8.37262679,15.1641904 9.0015212,16.0005527 L0.666666667,16 C0.2985,16 0,15.6743636 0,15.2727273 C0,14.8710909 0.2985,14.5454545 0.666666667,14.5454545 L1.33333333,14.5454545 L1.33333333,2.18181818 C1.33333333,0.978690909 2.23046667,0 3.33333333,0 Z M16,2.90909091 L16.0005527,9.0015212 C15.5986285,8.6992982 15.1498154,8.4562577 14.6671691,8.28531337 L14.6666667,4.36363636 L13.3333333,4.36363636 L13.3333333,2.90909091 L16,2.90909091 Z M10,2.8 L4,2.8 L4,7.2 L10,7.2 L10,2.8 Z"
            id="Combined-Shape"
            fill="#737373"
          />
          <path
            d="M13,10 C11.3431458,10 10,11.3431458 10,13 C10,14.6568542 11.3431458,16 13,16 C14.6568542,16 16,14.6568542 16,13 C16,12.2043505 15.6839295,11.4412888 15.1213203,10.8786797 C14.5587112,10.3160705 13.7956495,10 13,10 Z M14.5,13.5 L13.5,13.5 L13.5,14.5 L12.5,14.5 L12.5,13.5 L11.5,13.5 L11.5,12.5 L12.5,12.5 L12.5,11.5 L13.5,11.5 L13.5,12.5 L14.5,12.5 L14.5,13.5 Z"
            id="Shape"
            fill="#737373"
            fillRule="nonzero"
          />
        </g>
      </svg>
    );
  };

  const allFuels = watch('fuels') ?? [];

  const filledFuelIds = allFuels.map((fuel) => fuel.fuelTypeId).filter(Boolean);

  const maxFuelTypes = formOptions?.fuelTypeList?.length ?? 0;

  const canAddMoreFuels =
    watch('areaType') !== 'waypoint' &&
    !!watch('areaType') &&
    filledFuelIds.length < maxFuelTypes &&
    fields.length < maxFuelTypes;

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      {isLoading && <CmdLoader inContent={true} />}
      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-title">
          <a href="#" className="flex items-center">
            <i className="cmd-icon cmd-icon-back mr-2" onClick={handleConfirmDialog} />
            {f({ id: 'table.add' })}
          </a>
        </div>

        <div className="b-mc-waypoints-content h-[300px] overflow-y-auto">
          {!formOptions && <CmdLoader inContent={true} />}
          {isOpenConfirmDialog && (
            <ConfirmDialog
              title={`${f({
                id: 'waypoint.add.confirm.dialog.title',
              })}`}
              cancelCallback={handleCloseConfirmDialog}
              continueCallback={handleContinueConfirmDialog}
              cancelButtonTitle={f({
                id: 'waypoint.add.confirm.dialog.cancel',
              })}
              continueButtonTitle={f({
                id: 'waypoint.add.confirm.dialog.confirm',
              })}
            >
              {f({ id: 'waypoint.add.confirm.dialog.message' })}
            </ConfirmDialog>
          )}

          <Controller
            name="areaType"
            control={control}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                id="areaType"
                // name="customer"
                label={f({ id: 'enum.area.tab.title' })}
                // labelClassName="col-3 form-label"
                introOption={f({ id: 'form.choose' })}
                options={translateOptions(intlShape)}
                // rowClassName="row align-items-center"
                // columnClassName="col-9"
                searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                required={true}
                onChangeCallback={(value) => {
                  setValue('areaType', value);
                  trigger('areaType');
                  if (value === 'waypoint') {
                    clearErrors('fuels');
                    trigger();
                  }
                }}
              />
            )}
          />

          {selectedCustomers.length > 1 && customerFormData && (
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithSearchHookForm
                  {...field}
                  id="customer"
                  // name="customer"
                  label={f({ id: 'waypoints.form.customer.title' })}
                  // labelClassName="col-3 form-label"
                  introOption={f({ id: 'form.choose' })}
                  options={customerFormData}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                  control={control}
                  required={true}
                />
              )}
            />
          )}
          {selectedCustomers.length === 1 && customerFormData && (
            <CmdLabel
              id="customer"
              name="customer"
              title={f({ id: 'waypoints.form.customer.title' })}
              value={`${customerFormData[0].item} (${customerFormData[0].label})`}
            />
          )}
          {formOptions && formOptions.categoryId && (
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithIcon
                  {...field}
                  // name="icon"
                  // id="icon"
                  label={f({ id: 'waypoints.form.category.title' })}
                  options={formOptions.categoryId.values}
                  introOption={f({ id: 'form.choose' })}
                  control={control}
                  required={true}
                />
              )}
            />
          )}
          {formOptions && (
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  // name="name"
                  id="name"
                  type="text"
                  // labelClassName="col-3 form-label"
                  label={f({ id: 'waypoints.edit.title' })}
                  // newStyle={true}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  placeholder={f({ id: 'waypoints.edit.title.required.placeholder' })}
                  onChangeCallback={(value) => {
                    setValue('name', value);
                    trigger('name');
                  }}
                  required={true}
                  error={errors.name?.message}
                  touched={!!errors.name}
                />
              )}
            />
          )}

          {formOptions && formOptions.type && (
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="type"
                  // name="type"
                  label={f({ id: 'waypoints.edit.type' })}
                  // labelClassName="col-3 form-label"
                  introOption={
                    !watch('customer')
                      ? f({ id: 'waypoints.form.customer.pick' })
                      : f({ id: 'form.choose' })
                  }
                  options={[
                    ...formOptions.type.values.filter(
                      (item) =>
                        item.value === 'customer' &&
                        PermissionsService.hasAccess(
                          PERMISSION_WAYPOINT_CUSTOMER,
                          ['create'],
                          Number(watch('customer'))
                        )
                    ),
                    ...formOptions.type.values.filter(
                      (item) =>
                        item.value === 'private' &&
                        PermissionsService.hasAccess(
                          PERMISSION_WAYPOINT_PRIVATE,
                          ['create'],
                          Number(watch('customer'))
                        )
                    ),
                  ]}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  disabled={!watch('customer')}
                  onChangeCallback={(value) => {
                    setValue('type', value);
                    trigger('type');
                  }}
                  required={true}
                  error={errors.type?.message}
                  touched={true}
                />
              )}
            />
          )}
          {watch('areaType') !== 'waypoint' &&
            watch('areaType') &&
            formOptions &&
            formOptions?.refuelingTypeList && (
              <>
                <p className="text-sm font-semibold mb-3">
                  {f({ id: 'enum.waypoints.area.type.fuel' })}
                </p>

                <div className="area-divider" />

                <div className="mt-4">
                  <Controller
                    name="refuelingTypeList"
                    control={control}
                    render={({ field }) => (
                      <CmdSimpleSelect
                        {...field}
                        id="refuelingTypeList"
                        label={f({ id: 'enum.area.refuelingTypeList.title' })}
                        introOption={f({ id: 'form.choose' })}
                        options={formOptions.refuelingTypeList || []}
                        searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                        required={true}
                        onChangeCallback={(value) => {
                          setValue('refuelingTypeList', value);
                          trigger('refuelingTypeList');
                        }}
                      />
                    )}
                  />

                  {fields.map((item, index) => {
                    const allFuelsFields: { fuelTypeId: string; unitPriceIncVat: string }[] =
                      watch('fuels') ?? [];
                    const selectedFuelIds = allFuelsFields
                      .map((fuel, i) => i !== index && fuel.fuelTypeId)
                      .filter(Boolean);

                    const availableOptions = formOptions.fuelTypeList.filter(
                      (option) => !selectedFuelIds.includes(String(option.value))
                    );

                    return (
                      <div key={item.id} className="mb-4">
                        {index > 0 && (
                          <div
                            className="flex justify-end items-center gap-2 mt-1 text-[#737373] cursor-pointer"
                            onClick={() => handleRemoveFuel(index)}
                            data-cy={`area-remove-fuel-${index}`}
                          >
                            <i className="cmd-icon cmd-icon-bin" />
                            <span className="text-sm">{f({ id: 'table.deleteBtn' })}</span>
                          </div>
                        )}
                        <Controller
                          name={`fuels.${index}.fuelTypeId`}
                          control={control}
                          rules={{
                            required: true,
                          }}
                          render={({ field }) => (
                            <CmdSimpleSelect
                              {...field}
                              id={`fuels[${index}].fuelTypeId`}
                              label={f({ id: 'table_list.columns.fuelTypeId' })}
                              introOption={f({ id: 'form.choose' })}
                              options={availableOptions}
                              searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                              onChangeCallback={(value) => {
                                setValue(`fuels.${index}.fuelTypeId`, value);
                                trigger();
                              }}
                              required={true}
                              error={get(errors, `fuels.${index}.fuelTypeId`)?.message}
                              touched={true}
                            />
                          )}
                        />

                        <Controller
                          name={`fuels.${index}.unitPriceIncVat`}
                          control={control}
                          rules={{
                            required: true,
                          }}
                          render={({ field }) => (
                            <CmdInput
                              {...field}
                              id={`fuels[${index}].unitPriceIncVat`}
                              type="number"
                              label={f({
                                id: 'enum.refuelingImportRecords.calculatedValues.unitPriceIncVat',
                              })}
                              placeholder={f({ id: 'form.enterUnitPriceIncVat' })}
                              onChangeCallback={(value) => {
                                setValue(`fuels.${index}.unitPriceIncVat`, value);
                                trigger();
                              }}
                              required={true}
                              error={get(errors, `fuels.${index}.unitPriceIncVat`)?.message}
                              touched={true}
                            />
                          )}
                        />
                        {index < fields.length - 1 && <div className="area-divider" />}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

          {canAddMoreFuels && (
            <div className="b-waypoints-list-actions mt-23">
              <a
                href="#"
                className="e-button-sq m-0"
                title={f({ id: 'enum.area.form.add.fuel' })}
                onClick={handleAddFuel}
                data-cy="area-add-fuel"
              >
                {fuelIcon()}
                <span className="sr-only">{f({ id: 'enum.area.form.add.fuel' })}</span>
              </a>
            </div>
          )}

          {/* <div className="form-group">
                  <div className="row align-items-center">
                    <label htmlFor="" className="col-3 form-label">
                      Zakaznik
                    </label>
                    <div className="col-9">SimpleSelect with search</div>
                  </div>
                </div> */}
        </div>
        <div className="b-mc-waypoints-footer">
          <div className="row">
            <div className="col">
              <button className="e-button" onClick={handleConfirmDialog}>
                {f({ id: 'waypoints.cancel' })}
              </button>
            </div>
            <div className="col">
              <button
                type="submit"
                className={`${Object.keys(errors).length > 0 ? 'e-button e-button--gray  e-button--disabled' : 'e-button e-button--gray'}`}
                disabled={Object.keys(errors).length > 0}
              >
                {f({ id: 'waypoints.edit.save' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
